"use client";

import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { useIsPc } from "@/src/hooks";
import { buildOpenReplayLabel } from "@/src/utils/openreplay";
import { DiagnosisFindingCardList } from "@/src/components/DiagnosisFindingsPanel";
import type { DiagnosisFindingItem } from "@/src/components/DiagnosisFindingsPanel";
import { DiagnosisUnlockSceneBackground } from "../DiagnosisUnlockSceneBackground";
import {
  DIAGNOSIS_UNLOCK_STEP2_H5_CONTENT_MAX_WIDTH_PX,
  DIAGNOSIS_UNLOCK_STEP2_H5_HEADLINE_OFFSET_BELOW_SAFE_PX,
  DIAGNOSIS_UNLOCK_STEP2_H5_PANEL_GAP_CLASS,
  DIAGNOSIS_UNLOCK_STEP2_PC_CTA_TOP_CLASS,
  DIAGNOSIS_UNLOCK_STEP2_PC_PANEL_GAP_CLASS,
} from "../diagnosisUnlockLayoutTokens";
import { DIAGNOSIS_UNLOCK_MODAL_PC_MAX_HEIGHT } from "../diagnosisUnlockModalShell";
import type { DiagnosisUnlockStepChrome } from "../types";
import { DiagnosisUnlockDiagnosisStepSkeleton } from "./DiagnosisUnlockDiagnosisStepSkeleton";

export type DiagnosisUnlockDiagnosisStepProps = {
  /** 数据驱动顶栏 / 底栏 — 见 `diagnosisUnlockChrome.ts` */
  chrome: DiagnosisUnlockStepChrome;
  /** Step2 主标题 — 整段一句 `riskSummary` */
  riskSummary: string;
  findings: DiagnosisFindingItem[];
  /** Goal 详情加载中 — step2 骨架屏 */
  isLoading?: boolean;
  /** 进入 Step 3 */
  onPrimaryClick: () => void;
  /** 顶栏 close + dismiss — 由 chrome.topNav 决定是否渲染 */
  onDismiss?: () => void;
};

const PRIMARY_CTA_CLASS =
  "button_m flex h-11 w-full items-center justify-center rounded-lg bg-background_accent4_overlay100 text-content_inverse_primary transition-opacity hover:opacity-90 active:opacity-80";

function Step2FindingsContent({
  riskSummary,
  findings,
  className,
}: {
  riskSummary: string;
  findings: DiagnosisFindingItem[];
  className?: string;
}) {
  return (
    <div
      className={cn("flex w-full flex-col gap-10", className)}
      data-testid="diagnosis-findings-panel"
    >
      <h2
        className="headline_regular_m m-0 text-center leading-8 text-content_inverse_primary subpixel-antialiased"
        style={{
          WebkitFontSmoothing: "auto",
          display: "-webkit-box",
          WebkitLineClamp: 6,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {riskSummary}
      </h2>
      <DiagnosisFindingCardList findings={findings.slice(0, 3)} />
    </div>
  );
}

function DiagnosisStepCloseButton({
  onClose,
  className,
  testId = "diagnosis-report-close",
}: {
  onClose: () => void;
  className?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClose}
      className={className}
      data-testid={testId}
      data-openreplay-label={buildOpenReplayLabel(
        "Plan",
        "Diagnosis",
        "Close",
        "Close",
      )}
      aria-label="Close"
    >
      <Icon name="close" size={20} className="text-content_primary" />
    </button>
  );
}

/**
 * Step 2 — 诊断结果展示 + 主 CTA 进入 Step 3。
 *
 * 顶栏 / 底栏文案由 `chrome` 配置：
 * - Onboarding H5 `90772:39501`：无顶栏
 * - Plan report H5 `90772:39542`：关闭 → dismiss
 * - PC `90772:43086` / `43085`：同上逻辑
 */
export function DiagnosisUnlockDiagnosisStep({
  chrome,
  riskSummary,
  findings,
  isLoading = false,
  onPrimaryClick,
  onDismiss,
}: DiagnosisUnlockDiagnosisStepProps) {
  const [isPc] = useIsPc();
  const topNav = chrome.topNav;
  const showClose =
    topNav.variant === "close" &&
    topNav.behavior === "dismiss" &&
    onDismiss != null;
  const primaryLabel = chrome.primaryCta.label;
  const ctaClassName = cn(
    PRIMARY_CTA_CLASS,
    isLoading && "pointer-events-none opacity-50",
  );

  if (isPc) {
    return (
      <div
        className="relative flex w-full flex-col overflow-hidden rounded-3xl bg-background_solid_neutral_primary p-8 pb-safe"
        style={{ maxHeight: DIAGNOSIS_UNLOCK_MODAL_PC_MAX_HEIGHT }}
        data-testid="diagnosis-unlock-step-diagnosis-pc"
      >
        <DiagnosisUnlockSceneBackground variant="pc-modal" />

        {showClose ? (
          <div className="relative z-10 flex shrink-0 justify-end">
            <button
              type="button"
              onClick={onDismiss}
              className="flex size-6 items-center justify-center"
              data-testid="diagnosis-report-close"
              aria-label="Close"
            >
              <Icon
                name="close"
                size={24}
                className="text-content_inverse_primary"
              />
            </button>
          </div>
        ) : null}

        <div
          className={cn(
            "relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto",
            showClose && "-mt-2",
          )}
        >
          {isLoading ? (
            <DiagnosisUnlockDiagnosisStepSkeleton
              className={DIAGNOSIS_UNLOCK_STEP2_PC_PANEL_GAP_CLASS}
            />
          ) : (
            <Step2FindingsContent
              riskSummary={riskSummary}
              findings={findings}
              className={DIAGNOSIS_UNLOCK_STEP2_PC_PANEL_GAP_CLASS}
            />
          )}
        </div>

        <div
          className={cn(
            "relative z-10 shrink-0",
            DIAGNOSIS_UNLOCK_STEP2_PC_CTA_TOP_CLASS,
          )}
        >
          <button
            type="button"
            className={cn(ctaClassName, "mb-3")}
            data-testid="diagnosis-unlock-diagnosis-cta"
            data-openreplay-label={buildOpenReplayLabel(
              "Plan",
              "Diagnosis",
              "Next",
              primaryLabel,
            )}
            disabled={isLoading}
            onClick={onPrimaryClick}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex h-full min-h-0 w-full flex-col overflow-hidden pb-safe",
        showClose && "pt-safe",
      )}
      data-testid="diagnosis-unlock-step-diagnosis-h5"
    >
      <DiagnosisUnlockSceneBackground variant="h5-fullscreen" />

      {showClose ? (
        <div className="relative z-10 flex shrink-0 items-center px-4 py-2">
          <DiagnosisStepCloseButton
            onClose={onDismiss}
            className="flex size-10 items-center justify-center rounded-[20px] bg-background_solid_neutral_primary"
          />
        </div>
      ) : null}

      <div
        className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto px-6"
        style={
          showClose
            ? undefined
            : {
                paddingTop: `calc(env(safe-area-inset-top, 0px) + ${DIAGNOSIS_UNLOCK_STEP2_H5_HEADLINE_OFFSET_BELOW_SAFE_PX}px)`,
              }
        }
      >
        <div
          className={cn("mx-auto w-full", showClose && "pt-6")}
          style={{ maxWidth: DIAGNOSIS_UNLOCK_STEP2_H5_CONTENT_MAX_WIDTH_PX }}
        >
          {isLoading ? (
            <DiagnosisUnlockDiagnosisStepSkeleton
              className={DIAGNOSIS_UNLOCK_STEP2_H5_PANEL_GAP_CLASS}
            />
          ) : (
            <Step2FindingsContent
              riskSummary={riskSummary}
              findings={findings}
              className={DIAGNOSIS_UNLOCK_STEP2_H5_PANEL_GAP_CLASS}
            />
          )}
        </div>
      </div>

      <div className="relative z-10 shrink-0 px-4 pt-3">
        <button
          type="button"
          className={cn(ctaClassName, "mx-auto mb-3 max-w-[361px]")}
          data-testid="diagnosis-unlock-diagnosis-cta"
          disabled={isLoading}
          onClick={onPrimaryClick}
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}
